import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ChallengeHttpService } from '@src/app/core/http/challenge-http.service';
import { OrganizationHttpService } from '@src/app/core/http/organization-http.service';
import { TenantHttpService } from '@src/app/core/http/tenant.http.service';
import { CommunitySelectorStoredService } from '@src/app/core/services/community-selector-stored.service';
import { SessionService } from '@src/app/core/session.service';
import { untilDestroyed } from '@src/app/shared/functions/until-destroyed';
import { Category } from '@src/app/shared/interfaces/category.interface';
import { News } from '@src/app/shared/interfaces/news.interface';
import { OrganizationFilter } from '@src/app/shared/interfaces/organization.interface';
import { MediaBreakpoint } from '@src/app/shared/models/ui.model';
import { filter, Observable, Subscription, switchMap } from 'rxjs';

@Component({
  selector: 'app-categories-filter',
  templateUrl: './categories-filter.component.html',
})
export class CategoriesFilterComponent implements OnInit, OnChanges, OnDestroy {
  @Input() showLabel = false;
  @Input() showFilterBtn = true;
  @Input() multiple = false;
  @Input() postData: News;
  @Input() wrapperClass = '';
  @Input() appendTo = 'body';
  @Input() orgId: number;

  @Output() categoryFiltered = new EventEmitter();
  @Output() valueChanges = new EventEmitter();

  categoryForm: FormGroup;
  categories: Category[] = [];
  isFilterByChallenge = false;
  private categoriesSubscription: Subscription;

  get filterCount(): number {
    return Object.values(this.categoryForm.value).filter(
      (value) => value !== null
    ).length;
  }

  get isSmallScreen(): boolean {
    return window.innerWidth <= MediaBreakpoint.xl;
  }

  constructor(
    public readonly sessionService: SessionService,
    private readonly tenantHttpService: TenantHttpService,
    private readonly fb: FormBuilder,
    private readonly orgHttpService: OrganizationHttpService,
    private readonly communitySelectorService: CommunitySelectorStoredService,
    private readonly challengeHttpService: ChallengeHttpService
  ) {
    this.categoryForm = this.fb.group({});
  }

  ngOnInit(): void {
    this.communitySelectorService.selectedOrg
      .pipe(untilDestroyed(this), filter(Boolean))
      .subscribe((org: OrganizationFilter) => {
        this.isFilterByChallenge = org.isChallenge;
      });

    this.categoryForm.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((value) => {
        const dto = this.getFormattedDto();
        this.valueChanges.emit(dto);
        const shouldFilter = Object.keys(value).some((key) => {
          return !!value[key];
        });

        if (shouldFilter) {
          this.onSubmit();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.orgId) {
      this.getFilterCategories();
    }
  }

  getFilterCategories(): void {
    if (this.categoriesSubscription) {
      this.categoriesSubscription.unsubscribe();
    }

    let categories$: Observable<Category[]>;
    if (this.orgId) {
      categories$ = this.isFilterByChallenge
        ? this.challengeHttpService.getCategories(this.orgId)
        : this.orgHttpService.getCategories(this.orgId);
    } else {
      categories$ = this.sessionService.tenant$.pipe(
        filter(Boolean),
        switchMap((tenant) =>
          this.tenantHttpService.getTenantCategories(tenant.id)
        )
      );
    }

    this.categoriesSubscription = this.sessionService.apiReady$
      .pipe(
        untilDestroyed(this),
        filter(Boolean),
        switchMap(() => categories$)
      )
      .subscribe({
        next: (data: Category[]) => {
          this.categories = data;
          this.createFormControls();
          this.setCatgoryValueFromPost(this.postData);
        },
      });
  }

  onSubmit(): void {
    if (this.categoryForm.valid) {
      const dto = this.getFormattedDto();
      this.categoryFiltered.emit(dto);
    }
  }

  getFormattedDto() {
    let dto = [];

    this.categories.forEach((category) => {
      const controlName = category.name;
      let selectedTagIds = [];
      const controlValue = this.categoryForm.get(controlName)?.value;

      if (!controlValue) return;
      if (!this.multiple) {
        if (!Array.isArray(controlValue)) {
          selectedTagIds = [controlValue];
        } else {
          selectedTagIds = controlValue;
        }
      } else {
        selectedTagIds = controlValue;
      }

      if (selectedTagIds && selectedTagIds.length > 0) {
        dto.push({
          categoryId: category.id,
          tagIds: selectedTagIds,
        });
      }
    });

    return dto;
  }

  createFormControls(): void {
    this.categories.forEach((category) => {
      const controlName = category.name;
      this.categoryForm.addControl(controlName, this.fb.control(null));
    });
  }

  setCatgoryValueFromPost(post: News): void {
    if (!this.categoryForm || !this.categories.length || !post) return;
    const { categories } = post.content;
    categories.forEach((category) => {
      const controlName = this.categories.find(
        (item) => item.id === category.categoryId
      )?.name;

      const controlValue = category.tagIds;

      this.categoryForm.get(controlName)?.setValue(controlValue);
    });
  }

  hasFilters(): boolean {
    const formValue = this.categoryForm.value;

    const hasFilters = Object.keys(formValue).some((key) => {
      const value = formValue[key];

      if (value && Array.isArray(value)) {
        return value.length;
      } else {
        return value;
      }
    });

    return hasFilters;
  }

  resetAll(): void {
    this.categoryForm.reset();
    const dto = this.getFormattedDto();
    this.categoryFiltered.emit(dto);
  }

  shouldShowComma(item, controlName): boolean {
    const formValue = this.categoryForm.get(controlName).value;
    const idx = formValue.findIndex((i) => i === item.id);

    return idx + 1 !== formValue.length;
  }

  onClear(): void {
    const formValue = this.categoryForm.value;
    const shouldFilterOnClear = Object.keys(formValue).every(
      (key) => !formValue[key]
    );

    if (shouldFilterOnClear) {
      this.onSubmit();
    }
  }

  ngOnDestroy(): void {
    /** */
  }
}
